import { useState, useEffect } from "react";
import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useGetMyPayouts, useGetMySessions, useRefreshStripeConnect } from "@workspace/api-client-react";
import { formatCurrency } from "@/lib/utils";
import { DollarSign, TrendingUp, Calendar, CreditCard, AlertTriangle, CheckCircle, ExternalLink, Loader2 } from "lucide-react";
import { PayoutBreakdown } from "@/components/payout-breakdown";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";

export default function TrainerPayouts() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const { data: payouts, isLoading: payoutsLoading } = useGetMyPayouts({ query: { retry: false } });
  const { data: sessions } = useGetMySessions();
  const refreshStripe = useRefreshStripeConnect();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectError, setConnectError] = useState<{ message: string; isConnectNotEnabled: boolean } | null>(null);

  const searchParams = new URLSearchParams(window.location.search);
  const stripeStatus = searchParams.get("stripe");

  useEffect(() => {
    if (stripeStatus === "complete") {
      refreshUser();
    }
  }, [stripeStatus]);

  const completed = sessions?.filter((s: any) => s.status === "COMPLETED") || [];
  const totalEarned = completed.reduce((sum: number, s: any) => sum + (s.trainerPayout || 0), 0);
  const thisMonth = completed.filter((s: any) => {
    const d = new Date(s.proposedStart);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const monthlyEarnings = thisMonth.reduce((sum: number, s: any) => sum + (s.trainerPayout || 0), 0);

  const stripeConnected = !!user?.stripeConnectAccountId;
  const stripeOnboarded = !!user?.stripeConnectOnboarded;
  const payoutsEnabled = !!user?.stripeConnectPayoutsEnabled;

  const handleConnectStripe = async () => {
    setIsConnecting(true);
    setConnectError(null);
    try {
      const result = await refreshStripe.mutateAsync();
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err: any) {
      const raw = err?.message || "Please try again later.";
      const isConnectNotEnabled = raw.toLowerCase().includes("connect") && (raw.toLowerCase().includes("signed up") || raw.toLowerCase().includes("not enabled"));
      setConnectError({ message: raw, isConnectNotEnabled });
    } finally {
      setIsConnecting(false);
    }
  };

  const needsStripeSetup = !stripeConnected || !stripeOnboarded;

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Earnings & Payouts</h1>
          <p className="text-muted-foreground">Track your income and payout history</p>
        </div>

        {stripeStatus === "complete" && (
          <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-green-400 font-medium">Stripe setup completed!</p>
              <p className="text-xs text-muted-foreground mt-1">Your bank account has been connected. Payouts will be processed automatically after completed sessions.</p>
            </div>
          </div>
        )}

        {needsStripeSetup && stripeStatus !== "complete" && (
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardContent className="flex flex-col sm:flex-row items-start sm:items-center gap-4 py-6">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold">Bank account not connected</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {!stripeConnected
                    ? "Connect your bank account through Stripe to receive payouts for your training sessions."
                    : "Your Stripe account setup is incomplete. Please finish the onboarding process to enable payouts."}
                </p>
              </div>
              <Button onClick={handleConnectStripe} disabled={isConnecting} className="gap-2 shrink-0">
                {isConnecting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Setting up...</>
                ) : (
                  <><CreditCard className="w-4 h-4" /> {stripeConnected ? "Complete Setup" : "Connect Bank Account"}</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {connectError && (
          <Card className="border-red-500/30 bg-red-500/5">
            <CardContent className="py-5 flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-red-400 font-semibold text-sm">
                    {connectError.isConnectNotEnabled ? "Stripe Connect not enabled" : "Failed to start Stripe setup"}
                  </p>
                  {connectError.isConnectNotEnabled ? (
                    <p className="text-xs text-muted-foreground mt-1">
                      Your Stripe account hasn't signed up for Connect yet. Connect is required to receive payouts.{" "}
                      <a
                        href="https://dashboard.stripe.com/connect/accounts/overview"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline underline-offset-2 hover:text-primary/80 inline-flex items-center gap-1"
                      >
                        Enable Stripe Connect <ExternalLink className="w-3 h-3" />
                      </a>
                      {" "}in your Stripe dashboard, then click Connect Bank Account again.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">{connectError.message}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {stripeConnected && stripeOnboarded && !payoutsEnabled && stripeStatus !== "complete" && (
          <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-blue-400 font-medium">Stripe account connected — payouts pending</p>
              <p className="text-xs text-muted-foreground mt-1">Your account is being reviewed by Stripe. Payouts will be enabled once verification is complete.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Lifetime Earnings</CardDescription>
              <CardTitle className="text-3xl font-mono text-primary">{formatCurrency(totalEarned)}</CardTitle>
            </CardHeader>
            <CardContent><p className="text-xs text-muted-foreground">{completed.length} completed sessions</p></CardContent>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">This Month</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">{formatCurrency(monthlyEarnings)}</CardTitle>
            </CardHeader>
            <CardContent><p className="text-xs text-muted-foreground">{thisMonth.length} sessions</p></CardContent>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Avg Per Session</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">{formatCurrency(completed.length > 0 ? totalEarned / completed.length : 0)}</CardTitle>
            </CardHeader>
            <CardContent><p className="text-xs text-muted-foreground">Average payout</p></CardContent>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Hourly Rate</CardDescription>
              <CardTitle className="text-3xl font-mono text-primary">{formatCurrency(user?.hourlyRate || 0)}</CardTitle>
            </CardHeader>
            <CardContent><p className="text-xs text-muted-foreground">Current rate</p></CardContent>
          </Card>
        </div>

        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Recent Completed Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            {completed.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No completed sessions yet</p>
            ) : (
              <div className="space-y-3">
                {completed.slice(0, 20).map((session: any) => (
                  <div key={session.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/5 border border-transparent hover:border-white/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-white">{session.student?.firstName} {session.student?.lastName}</p>
                        <p className="text-xs text-muted-foreground">{format(new Date(session.proposedStart), "MMM d, yyyy · h:mm a")}</p>
                      </div>
                    </div>
                    <PayoutBreakdown session={session} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
